import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { environment } from "@uw-env/environment";
import { combineLatest, Observable, of, map } from 'rxjs';
import { Alert } from "../models/alert.model";
import { Content, CreateContentRequest } from "../models/content.model";
import { WebPage } from "../models/web-page.model";
import { WebPart } from "../models/web-part.model";
import { Email, EmailType } from "../models/email.model";

@Injectable({
  providedIn: "root"
})
export class ContentService {

  private readonly IMAGE_WEB_PART_NAMES = [
    'Logo_1',
    'Logo_2',
    'Welcome_SideImage',
    'Metric_1_Image',
    'Metric_2_Image',
    'Metric_3_Image',
    'Metric_4_Image',
    'AboutUs_Image',
    'CampaignClosed_SideImage',
    'MyCampaign_Image',
  ]

  constructor(
    private readonly _client: HttpClient,
    private readonly _translateService: TranslateService
  ) { }

  getImageWebPartIds(): Observable<string[]> {

    return this.webParts().pipe(
      map(webParts => {
        return webParts.filter(wp => this.IMAGE_WEB_PART_NAMES.includes(wp.name)).map(wp =>`${wp.id}`)
      })
    );
  }

  webPages(): Observable<WebPage[]> {
    return this._client.get<{ data: WebPage[] }>(`${environment.apiURL}/administration/content/webpages`)
      .pipe(
        map(response => response.data)
      );
  }

  webParts(): Observable<WebPart[]> {
    return this._client.get<{ data: WebPart[] }>(`${environment.apiURL}/administration/content/webparts`)
      .pipe(
        map(response => {
          // TODO REMOVE. TEST MOCK CODE 
          // let additionalWebParts = [];
          // additionalWebParts.push(
          //   {
          //     id: '38',
          //     name: 'SysCredCardDecMsg',
          //     display: 'System related credit card decline message',
          //   },
          //   {
          //     id: '39',
          //     name: 'GtwyCredCardDecMsg',
          //     display: 'Non Payment gateway Credit Card decline message',
          //   }
          // );
          // response.data.push(...additionalWebParts);
          return response.data
        })
      );
  }

  getContentItems(profileId: string, windowId: string, pageName: string): Observable<Content[]> {
    return this._client.get<{ data: Content[] }>(`${environment.apiURL}/administration/content/profile/${profileId}/window/${windowId}/Page/${pageName}/contentItems`)
      .pipe(
        map(response => response.data)
      );
  }


  getContentItemsByCampaign(campaign: any, pageName: string, windowId: string): Observable<Content[]> {
    const url = `${environment.apiURL}/administration/content/profile/${campaign.profileId}/campaign/${campaign.campaignId}/window/${windowId}/Page/${pageName}/contentItems`;

    return this._client.get<{ data: Content[] }>(`${environment.apiURL}/administration/content/profile/${campaign.profileId}/campaign/${campaign.campaignId}/window/${windowId}/Page/${pageName}/contentItems`)
      .pipe(
        map(res => res.data)
      );
  }

  getContentById(profileId: string, windowId: any, pageName: any, contentId: any): Observable<Content> {
    return this.getContentItems(profileId, windowId, pageName)
      .pipe(
        map(x => x.filter(x => x.id == contentId)[0])
      )
  }

  getCampaignContentById(campaign: any, pageName: any, windowId: any, contentId: any): Observable<Content> {
    return this.getContentItemsByCampaign(campaign, pageName, windowId)
      .pipe(
        map(x => x.filter(x => x.id == contentId)[0])
      )
  }


  createContent(profileId: string, windowId: number, pageName: string, content: CreateContentRequest): Observable<Content> {
    return this._client.post<{ data: Content }>(`${environment.apiURL}/administration/content/profile/${profileId}/window/${windowId}/Page/${pageName}/contentItems`, content)
      .pipe(
        map(response => response.data)
      );
  }

  updateContent(profileId: string, content: Content): Observable<void> {
    return this._client.put<void>(`${environment.apiURL}/administration/content/profile/${profileId}/contentItem/${content.id}`, content);
  }

  deleteContent(profileId: string, contentId: number): Observable<void> {
    return this._client.delete<void>(`${environment.apiURL}/administration/content/profile/${profileId}/contentItem/${contentId}`);
  }

  deleteCampaignContent(campaign: any, contentId: any) {
    return this._client.delete<void>(`${environment.apiURL}/administration/content/profile/${campaign.profileId}/campaign/${campaign.campaignId}/contentItem/${contentId}`);
  }

  createCampaignContent(campaign: any, pageName: string, windowId: number, content: CreateContentRequest): Observable<Content> {
    const url = `${environment.apiURL}/administration/content/profile/${campaign.profileId}/campaign/${campaign.campaignId}/window/${windowId}/Page/${pageName}/contentItems`;
    return this._client.post<{ data: Content }>(url, content)
      .pipe(
        map(res => res.data)
      );
  }

  updateCampaignContent(campaign: any, content: Content): Observable<Content> {
    return this._client.put<{ data: Content }>(`${environment.apiURL}/administration/content/profile/${campaign.profileId}/campaign/${campaign.campaignId}/contentItem/${content.id}`, content)
      .pipe(
        map(res => res.data)
      )
  }

  deletCampaignContent(campaign: any, contentId: number): Observable<void> {
    return this._client.delete<void>(`${environment.apiURL}/administration/content/profile/${campaign.profileId}/campaign/${campaign.campaignId}/contentItem/${contentId}`);
  }

  getAlertsByPageName(profileId: any, pageName: string): Observable<Alert[]> {
    return this._client.get<{ data: Content[] }>(`${environment.apiURL}/administration/content/profile/${profileId}/page/${pageName}/alerts`).pipe(
      map(response => {
        return response.data.map(c => <Alert>{
          id: c.id,
          description: c.description,
          content: c.contentXML,
          contentFr: c.contentXML_fr
        });
      })
    )
  }

  getCampaignAlertsByPageName(campaignId: any, pageName: string): Observable<Alert[]> {
    return this._client.get<{ alerts: Alert[] }>(`${environment.apiURL}/administration/content/campaign/${campaignId}/page/${pageName}/alerts`).pipe(
      map(response => response.alerts)
    )
  }

  updateAlert(profileId: any, alert: Alert): Observable<any> {
    const url = `${environment.apiURL}/administration/content/profile/${profileId}/alert/${alert.id}`;

    return combineLatest([
      this._client.put<void>(url, {
        content: alert.content,
        isFrench: false
      }),
      this._client.put<void>(url, {
        content: alert.contentFr,
        isFrench: true
      })
    ]);
  }

  updateCampaignAlert(campaignId: any, alert: Alert) {
    const url = `${environment.apiURL}/administration/content/campaign/${campaignId}/alert/${alert.id}`;

    return combineLatest([
      this._client.put<void>(url, {
        content: alert.content,
        isFrench: false
      }),
      this._client.put<void>(url, {
        content: alert.contentFr,
        isFrench: true
      })
    ]);
  }

  getEmails(): Observable<any[]> {

    return of([
      { "emailId": 1, "emailTypeId": 1, "filterId": null, "content": "<h1> MajorGift Content</h1>", "language": "en-CA", "locked": 1, "senderName": "Quinntyne", "footer": null, "subject": "Mock", "note": " MajorGift  Seed", "replyTo": "quinntynebrown@gmail.com" },
      { "emailId": 2, "emailTypeId": 2, "filterId": null, "content": "<h1> MajorGiftWithdrawn Content</h1>", "language": "en-CA", "locked": 1, "senderName": "Quinntyne", "footer": null, "subject": "Mock", "note": " MajorGiftWithdrawn  Seed", "replyTo": "quinntynebrown@gmail.com" },
      { "emailId": 3, "emailTypeId": 3, "filterId": null, "content": "<h1> reminder Content</h1>", "language": "en-CA", "locked": 1, "senderName": "Quinntyne", "footer": "<span>  reminder Footer</span>", "subject": "Mock", "note": "reminder  Seed", "replyTo": "quinntynebrown@gmail.com" },
      { "emailId": 4, "emailTypeId": 4, "filterId": null, "content": "<h1> requestAdminPasswordReset Content</h1>", "language": "en-CA", "locked": 1, "senderName": "Quinntyne", "footer": null, "subject": "Mock", "note": " requestAdminPasswordReset  Seed", "replyTo": "quinntynebrown@gmail.com" },
      { "emailId": 5, "emailTypeId": 5, "filterId": null, "content": "<h1> RequestPaperPledge Content</h1>", "language": "en-CA", "locked": 1, "senderName": "Quinntyne", "footer": null, "subject": "Mock", "note": " RequestPaperPledge  Seed", "replyTo": "quinntynebrown@gmail.com" },
      { "emailId": 6, "emailTypeId": 6, "filterId": null, "content": "<h1> requestPasswordReset Content</h1>", "language": "en-CA", "locked": 1, "senderName": "Quinntyne", "footer": null, "subject": "Mock", "note": " requestPasswordReset  Seed", "replyTo": "quinntynebrown@gmail.com" },
      { "emailId": 7, "emailTypeId": 7, "filterId": null, "content": "<h1> ThankYou Content</h1>", "language": "en-CA", "locked": 1, "senderName": "Quinntyne", "footer": "<span>  ThankYou Footer</span>", "subject": "Mock", "note": " ThankYou  Seed", "replyTo": "quinntynebrown@gmail.com" },
      { "emailId": 8, "emailTypeId": 8, "filterId": null, "content": "<h1> ThankYouForEdit Content</h1>", "language": "en-CA", "locked": 1, "senderName": "Quinntyne", "footer": "<span>  ThankYouForEdit Footer</span>", "subject": "Mock", "note": " ThankYouForEdit  Seed", "replyTo": "quinntynebrown@gmail.com" },
      { "emailId": 9, "emailTypeId": 9, "filterId": null, "content": "<h1> Welcome Content</h1>", "language": "en-CA", "locked": 1, "senderName": "Quinntyne", "footer": "<span>  Welcome Footer</span>", "subject": "Mock", "note": " Welcome  Seed", "replyTo": "quinntynebrown@gmail.com" }
    ]);
  }

  getEmailById(emailId: any): Observable<Email> {
    return this.getEmails().pipe(
      map(emails => emails.filter(x => x.emailId == emailId)[0])
    )
  }

  updateEmail(email: Email): Observable<boolean> {
    return of(true);
  }

  getEmailTypes(): Observable<string[]> {
    return of([
      "Broadcast",
      "DonationDeletion",
      "GOS",
      "MajorGift",
      "MajorGiftNew",
      "MajorGiftWithdrawn",
      "Reminder",
      "RequestAdminPasswordReset",
      "RequestPaperPledge",
      "RequestPasswordReset",
      "ThankYou",
      "ThankYouForEdit",
      "ThankYouForAPRRollOver",
      "Welcome",
      "VoidTaxReceipt",
    ]);
  }
}
